(() => {
  const rootSelector = '[data-bp-mystery-builder]';
  const BUILDER_VERSION = '1.0.0';

  if (window.BPMysteryBuilder) {
    window.BPMysteryBuilder.initialize(document);
    return;
  }

  const Core = window.BPMysteryBuilderCore;
  if (!Core) return;

  const instances = new WeakMap();
  const instancesBySectionId = new Map();

  class MysteryBuilder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.isDesignMode = root.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
      this.storageKey = `bp:mystery-builder:v${root.dataset.storageVersion || '1'}:${this.sectionId}`;
      this.abortController = new AbortController();
      this.submissionGate = Core.createSubmissionGate();
      this.productPanels = Array.from(root.querySelectorAll('[data-bp-product-panel]'));
      this.steps = new Map(
        Array.from(root.querySelectorAll('[data-bp-builder-step]')).map((step) => [step.dataset.bpBuilderStep, step]),
      );
      this.summary = root.querySelector('[data-bp-builder-summary]');
      this.navigation = root.querySelector('[data-bp-builder-navigation]');
      this.backButton = root.querySelector('[data-bp-back]');
      this.continueButton = root.querySelector('[data-bp-continue]');
      this.addButton = root.querySelector('[data-bp-add-to-cart]');
      this.liveRegion = root.querySelector('[data-bp-builder-live]');
      this.cartStatus = root.querySelector('[data-bp-cart-status]');
      this.customerError = root.querySelector('[data-bp-customer-error]');
      this.requestBox = root.querySelector('[data-bp-summary-request]');
      this.requestField = root.querySelector('[data-bp-request]');
      this.requestCount = root.querySelector('[data-bp-request-count]');
      this.progress = root.querySelector('[data-bp-progress]');
      this.progressBar = root.querySelector('[data-bp-progress-bar]');
      this.progressText = root.querySelector('[data-bp-progress-text]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.state = Core.sanitizeState();
      this.currentView = 'category';
      this.visualAnimations = new Set();
    }

    initialize() {
      this.state = this.isDesignMode ? Core.sanitizeState() : this.readStoredState();
      this.currentView = this.isDesignMode ? this.normalizeView(this.root.dataset.previewStep) : 'category';

      if (this.root.hasAttribute('data-bp-journey')) this.journey = window.BPMysteryJourney?.(this);
      this.bindEvents();
      this.updateRequestCount();
      this.syncAll();
      this.showView(this.currentView, { focus: false, history: 'replace', announce: false, emit: false });
      this.root.classList.add('is-ready');
      this.root.dataset.bpBuilderReady = 'true';
      this.observeDock();
      window.BPUniverse?.initialize(this.root);
      this.emit('bp:mystery_builder_open');
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.root.addEventListener('change', (event) => this.handleChange(event), { signal });
      this.root.addEventListener('input', (event) => {
        if (event.target === this.requestField) {
          this.updateRequestCount();
          this.setAtelierSuccess(false);
        }
      }, { signal });
      this.root.addEventListener('click', (event) => this.handleClick(event), { signal });
      window.addEventListener('popstate', (event) => this.handlePopState(event), { signal });
    }

    handleChange(event) {
      this.setAtelierSuccess(false);
      const option = event.target.closest('[data-bp-option-input]');
      if (option && this.root.contains(option)) {
        const group = option.dataset.optionGroup;
        if (group === 'category') {
          this.state = Core.sanitizeState({ category: option.value });
        } else if (group === 'tier') {
          this.state = Core.sanitizeState({ ...this.state, tier: option.value, size: null });
        } else if (group === 'size') {
          this.state = Core.sanitizeState({ ...this.state, size: option.value });
        }

        this.persistState();
        this.syncAll();
        this.emit('bp:mystery_option_select', { step: group });
        return;
      }

      const complexion = event.target.closest('[data-bp-complexion-input]');
      if (complexion && this.root.contains(complexion)) {
        this.state = Core.sanitizeState({ ...this.state, [complexion.dataset.bpComplexionInput]: complexion.value });
        this.persistState();
        this.syncAll();
        this.emit('bp:mystery_option_select', { step: 'complexion' });
        return;
      }

      if (event.target.matches('[data-bp-base-opt-out]')) {
        this.state = Core.sanitizeState({ ...this.state, baseFaceOptOut: event.target.checked });
        this.persistState();
        this.syncAll();
        this.emit('bp:mystery_option_select', { step: 'complexion' });
      }
    }

    handleClick(event) {
      if (this.journey?.click(event)) return;
      const compactEdit = event.target.closest('[data-bp-compact-edit]');
      const summaryEdit = event.target.closest('[data-bp-edit-step]');

      if (compactEdit && this.root.contains(compactEdit)) {
        this.showView(compactEdit.dataset.bpCompactEdit, { focus: true, history: 'push', announce: true });
        return;
      }

      if (summaryEdit && this.root.contains(summaryEdit)) {
        this.showView(summaryEdit.dataset.bpEditStep, { focus: true, history: 'push', announce: true });
        return;
      }

      if (event.target.closest('[data-bp-back]')) {
        this.goBack();
        return;
      }

      if (event.target.closest('[data-bp-continue]')) {
        this.goForward();
        return;
      }

      if (event.target.closest('[data-bp-add-to-cart]')) this.addToCart();
    }

    goBack() {
      const steps = Core.getStepKeys(this.state.category);
      const index = steps.indexOf(this.currentView);

      if (this.currentView === 'summary') {
        this.showView(steps[steps.length - 1], { focus: true, history: 'push', announce: true });
      } else if (index > 0) {
        this.showView(steps[index - 1], { focus: true, history: 'push', announce: true });
      }
    }

    goForward() {
      if (!this.isCurrentStepComplete()) return;

      const steps = Core.getStepKeys(this.state.category);
      const index = steps.indexOf(this.currentView);
      if (index < steps.length - 1) {
        this.showView(steps[index + 1], { focus: true, history: 'push', announce: true });
      } else {
        this.showView('summary', { focus: true, history: 'push', announce: true });
      }
    }

    isCurrentStepComplete() {
      if (this.currentView === 'category') return Boolean(this.state.category && this.categoryIsConfigured(this.state.category));
      if (this.currentView === 'tier') return Boolean(this.state.tier && this.getSelectedProductPanel()?.dataset.configured === 'true');
      if (this.currentView === 'size') {
        const variant = this.getSelectedVariant();
        return Core.validateVariantData(variant, {
          productConfigured: this.getSelectedProductPanel()?.dataset.configured === 'true',
          requireAvailable: true,
        }).valid;
      }
      if (this.currentView === 'complexion') return Boolean(this.state.baseFaceOptOut || (this.state.skinTone && this.state.undertone));
      return false;
    }

    syncAll() {
      this.ensureStateStillAvailable();
      if (this.requestBox) {
        const valid = Core.validateVariantData(this.getSelectedVariant(), {
          productConfigured: this.getSelectedProductPanel()?.dataset.configured === 'true',
          requireAvailable: true,
        }).valid;
        this.requestBox.hidden = this.currentView !== 'summary' || !valid;
      }
      this.syncInputs();
      this.syncProductAvailability();
      this.syncProductPanel();
      this.syncCompactSummary();
      this.syncNavigation();
      this.syncAtelier();
      if (this.currentView === 'summary') this.renderSummary();
    }

    ensureStateStillAvailable() {
      if (this.state.category && !this.categoryIsConfigured(this.state.category)) {
        this.state = Core.sanitizeState();
        return;
      }

      const panel = this.getSelectedProductPanel();
      if (this.state.tier && (!panel || panel.dataset.configured !== 'true')) {
        this.state = Core.sanitizeState({ category: this.state.category });
        return;
      }

      if (this.state.size) {
        const variant = this.getSelectedVariant();
        if (!variant || !Core.validateVariantData(variant, { productConfigured: true, requireAvailable: true }).valid) {
          this.state = Core.sanitizeState({ ...this.state, size: null });
        }
      }

      if (this.state.category === 'skincare') {
        this.state = Core.sanitizeState({ ...this.state, skinTone: null, undertone: null, baseFaceOptOut: false });
      }
    }

    syncInputs() {
      this.root.querySelectorAll('[data-bp-option-input]').forEach((input) => {
        const group = input.dataset.optionGroup;
        input.checked = group === 'category'
          ? input.value === this.state.category
          : group === 'tier'
            ? input.value === this.state.tier
            : input.value === this.state.size && input.dataset.productKey === this.getSelectedProductPanel()?.dataset.productKey;
      });

      this.root.querySelectorAll('[data-bp-complexion-input]').forEach((input) => {
        input.checked = input.value === this.state[input.dataset.bpComplexionInput];
      });

      const baseOptOut = this.root.querySelector('[data-bp-base-opt-out]');
      if (baseOptOut) baseOptOut.checked = this.state.baseFaceOptOut;
    }

    syncProductAvailability() {
      this.root.querySelectorAll('[data-option-group="category"]').forEach((input) => {
        const unavailable = !this.categoryIsConfigured(input.value);
        input.disabled = unavailable;
        input.setAttribute('aria-disabled', String(unavailable));
      });

      const categoryUnavailable = this.root.querySelector('[data-bp-category-unavailable]');
      if (categoryUnavailable) {
        const allCategoriesAvailable = Core.CATEGORIES.every((category) => this.categoryIsConfigured(category));
        categoryUnavailable.hidden = allCategoriesAvailable;
      }

      this.root.querySelectorAll('[data-option-group="tier"]').forEach((input) => {
        const panel = this.getProductPanel(this.state.category, input.value);
        const unavailable = !this.state.category || panel?.dataset.configured !== 'true';
        input.disabled = unavailable;
        input.setAttribute('aria-disabled', String(unavailable));
      });

      const tierUnavailable = this.root.querySelector('[data-bp-tier-unavailable]');
      if (tierUnavailable) {
        const allTiersAvailable = Core.TIERS.every(
          (tier) => this.getProductPanel(this.state.category, tier)?.dataset.configured === 'true',
        );
        tierUnavailable.hidden = !this.state.category || allTiersAvailable;
      }
    }

    syncProductPanel() {
      const selectedPanel = this.getSelectedProductPanel();
      const showEditorMatrix = this.isDesignMode && this.currentView === 'size' && !selectedPanel;
      this.productPanels.forEach((panel) => {
        panel.hidden = showEditorMatrix ? false : panel !== selectedPanel;
      });

      const instruction = this.root.querySelector('[data-bp-size-instruction]');
      if (instruction) instruction.hidden = Boolean(selectedPanel) || showEditorMatrix;
    }

    syncCompactSummary() {
      const values = {
        category: this.labelForCategory(this.state.category),
        tier: this.labelForTier(this.state.tier),
        size: this.getSelectedVariant()?.sizeLabel || '',
      };

      Object.entries(values).forEach(([key, value]) => {
        const button = this.root.querySelector(`[data-bp-compact-edit="${key}"]`);
        const target = this.root.querySelector(`[data-bp-compact-value="${key}"]`);
        if (target) target.textContent = value;
        if (button) button.hidden = !value || key === this.currentView;
      });
    }

    syncNavigation() {
      if (!this.navigation) return;
      const steps = Core.getStepKeys(this.state.category);
      const index = steps.indexOf(this.currentView);
      this.navigation.hidden = this.currentView === 'summary';
      if (this.backButton) this.backButton.hidden = index <= 0;
      if (this.continueButton) {
        const enabled = this.isCurrentStepComplete();
        this.continueButton.disabled = !enabled;
        this.continueButton.setAttribute('aria-disabled', String(!enabled));
      }
    }

    showView(requestedView, options = {}) {
      const view = this.normalizeView(requestedView);
      const previousView = this.currentView;
      this.currentView = view;
      if (this.journey && view === 'complexion') {
        this.state = Core.sanitizeState({ ...this.state, skinTone: this.state.skinTone || 'unknown', undertone: this.state.undertone || 'unknown' });
        this.persistState();
      }

      this.steps.forEach((step, key) => {
        step.hidden = view === 'summary' || key !== view;
      });
      if (this.summary) this.summary.hidden = view !== 'summary';

      this.syncAll();
      this.updateProgress();

      if (view === 'summary') {
        this.renderSummary();
        if (options.emit !== false) this.emit('bp:mystery_summary_view', { step: 'summary' });
      } else if (options.emit !== false) {
        this.emit('bp:mystery_step_view', { step: view });
      }

      if (options.history === 'push') this.writeHistory('push');
      if (options.history === 'replace') this.writeHistory('replace');
      if (options.announce) this.announceCurrentView();
      if (options.focus) this.focusCurrentHeading();
      if (options.focus && previousView !== view) this.animateAtelierView(previousView);
    }

    syncAtelier() {
      if (this.journey) { this.journey.render(); return; }
      if (!this.root.hasAttribute('data-bp-atelier')) return;
      const variant = this.getSelectedVariant();
      const panel = this.getSelectedProductPanel();
      const valid = Core.validateVariantData(variant, { productConfigured: panel?.dataset.configured === 'true', requireAvailable: true }).valid;
      const label = [this.labelForCategory(this.state.category), this.labelForTier(this.state.tier), variant?.sizeLabel].filter(Boolean).join(' · ');
      const put = (selector, text) => {
        const node = this.root.querySelector(selector);
        if (node && node.textContent !== String(text || '')) node.textContent = text || '';
      };
      this.root.dataset.atelierView = this.currentView;
      this.root.dataset.atelierCategory = this.state.category || '';
      this.root.dataset.atelierTier = this.state.tier || '';
      this.root.dataset.atelierSize = this.state.size || '';
      put('[data-bp-atelier-composition]', label || this.root.dataset.atelierEmpty);
      put('[data-bp-atelier-price]', valid ? variant.priceFormatted : '');
      put('[data-bp-atelier-value]', valid ? variant.minimumValueFormatted : '');
      put('[data-bp-atelier-count]', valid ? this.formatQuantity(variant) : '');
      put('[data-bp-atelier-dock-label]', valid ? this.root.dataset.atelierReady : label || this.root.dataset.atelierEmpty);
      put('[data-bp-atelier-dock-price]', valid ? variant.priceFormatted : '');
      put('[data-bp-atelier-dock-promise]', valid ? this.formatQuantity(variant) : '');
      this.root.querySelectorAll('[data-bp-atelier-receipt], [data-bp-atelier-track]').forEach(node => { node.hidden = !valid; });
      const track = this.root.querySelector('[data-bp-atelier-track]');
      if (track && valid) track.style.setProperty('--bp-paid-ratio', `${Math.min(100, Number(variant.priceCents) / Number(variant.minimumValueCents) * 100)}%`);
      const checkout = this.root.querySelector('[data-bp-atelier-checkout]');
      if (checkout) checkout.hidden = this.currentView !== 'summary';
      if (this.continueButton) {
        this.continueButton.dataset.defaultLabel ||= this.continueButton.textContent;
        const steps = Core.getStepKeys(this.state.category);
        this.continueButton.textContent = this.currentView === steps[steps.length - 1] ? this.root.dataset.atelierReview : this.continueButton.dataset.defaultLabel;
      }
    }

    animateAtelierView(previousView) {
      if (!this.root.hasAttribute('data-bp-atelier') || this.reducedMotion.matches || this.root.classList.contains('bp-mystery-builder--no-motion')) return;
      this.visualAnimations.forEach(animation => animation.cancel());
      this.visualAnimations.clear();
      const target = this.currentView === 'summary' ? this.summary : this.steps.get(this.currentView);
      if (!target?.animate) return;
      const order = [...Core.getStepKeys(this.state.category), 'summary'];
      const direction = order.indexOf(this.currentView) >= order.indexOf(previousView) ? 1 : -1;
      const animation = target.animate([{ opacity: 0.35, transform: `translateX(${direction * 10}px)` }, { opacity: 1, transform: 'translateX(0)' }], { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' });
      this.visualAnimations.add(animation);
      animation.onfinish = () => this.visualAnimations.delete(animation);
      animation.oncancel = () => this.visualAnimations.delete(animation);
    }

    observeDock() {
      const dock = this.root.querySelector('[data-bp-atelier-dock]');
      if (!dock || typeof IntersectionObserver !== 'function') return;
      this.dockObserver = new IntersectionObserver(entries => {
        dock.classList.toggle('is-outside', !entries[0].isIntersecting);
      });
      this.dockObserver.observe(this.root);
    }

    setAtelierSuccess(success) {
      const node = this.root.querySelector('[data-bp-atelier-success]');
      if (node) node.hidden = !success;
      if (!success) this.setCartStatus('');
    }

    normalizeView(candidate) {
      const steps = Core.getStepKeys(this.state.category);
      if (candidate === 'summary') return 'summary';
      if (steps.includes(candidate)) return candidate;
      return steps[0];
    }

    updateProgress() {
      const steps = Core.getStepKeys(this.state.category);
      const current = this.currentView === 'summary' ? steps.length : Math.max(1, steps.indexOf(this.currentView) + 1);
      const total = steps.length;
      const percentage = `${(current / total) * 100}%`;

      if (this.progressText) {
        this.progressText.textContent = this.interpolate(this.root.dataset.progressTemplate, { current, total });
      }
      if (this.progress) {
        this.progress.setAttribute('aria-valuemax', String(total));
        this.progress.setAttribute('aria-valuenow', String(current));
      }
      this.progressBar?.style.setProperty('--bp-progress', percentage);
      this.progressBar?.parentElement?.style.setProperty('--bp-progress', percentage);
    }

    announceCurrentView() {
      if (this.journey) { this.liveRegion.textContent = this.root.querySelector('[data-heading]').textContent; return; }
      if (!this.liveRegion) return;
      const heading = this.currentView === 'summary'
        ? this.summary?.querySelector('[data-bp-summary-title]')
        : this.steps.get(this.currentView)?.querySelector('[data-bp-step-title]');
      this.liveRegion.textContent = heading?.textContent?.trim() || '';
    }

    focusCurrentHeading() {
      if (this.journey) {
        const heading = this.root.querySelector('[data-heading]');
        heading.focus({ preventScroll: true });
        heading.closest('[data-intro]').scrollIntoView({ block: 'start', behavior: 'instant' });
        return;
      }
      const heading = this.currentView === 'summary'
        ? this.summary?.querySelector('[data-bp-summary-title]')
        : this.steps.get(this.currentView)?.querySelector('[data-bp-step-title]');
      if (!heading) return;

      heading.focus({ preventScroll: true });
      heading.scrollIntoView({
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }

    renderSummary() {
      const panel = this.getSelectedProductPanel();
      const variant = this.getSelectedVariant();
      const validation = Core.validateVariantData(variant, {
        productConfigured: panel?.dataset.configured === 'true',
        requireAvailable: true,
      });
      const valid = validation.valid;

      this.setSummaryField('category', this.labelForCategory(this.state.category));
      this.setSummaryField('tier', this.labelForTier(this.state.tier));
      this.setSummaryField('size', variant?.sizeLabel || '');
      this.setSummaryField('price', variant?.priceFormatted || '');
      this.setSummaryField('minimum-value', variant?.minimumValueFormatted || '');
      this.setSummaryField('quantity', variant ? this.formatQuantity(variant) : '');
      this.setSummaryField('availability', variant?.available ? variant.availabilityLabel : this.root.dataset.customerUnavailable);
      this.setSummaryField('guaranteed', panel?.dataset.guaranteed || '');
      this.setSummaryField('surprise', panel?.dataset.surprise || '');
      this.setSummaryField(
        'duplicates',
        panel?.dataset.allowDuplicates === 'true'
          ? this.root.dataset.labelDuplicatesYes
          : this.root.dataset.labelDuplicatesNo,
      );

      const showComplexion = this.state.category !== 'skincare';
      const complexionSummary = this.root.querySelector('[data-bp-summary-complexion]');
      if (complexionSummary) complexionSummary.hidden = !showComplexion;
      if (showComplexion) {
        this.setSummaryField('skin-tone', this.labels().skinTones[this.state.skinTone] || '');
        this.setSummaryField('undertone', this.labels().undertones[this.state.undertone] || '');
        this.setSummaryField(
          'base-face',
          this.state.baseFaceOptOut ? this.root.dataset.labelBaseExcluded : this.root.dataset.labelBaseNoExclusion,
        );
      }

      const rangeNote = this.root.querySelector('[data-bp-summary-range]');
      if (rangeNote) rangeNote.hidden = variant?.countMode !== 'range';
      const rangeShort = this.root.querySelector('[data-bp-range-short]');
      const rangeExtended = this.root.querySelector('[data-bp-range-extended]');
      if (rangeShort) rangeShort.textContent = this.root.dataset.disclosureShort || '';
      if (rangeExtended) rangeExtended.textContent = this.root.dataset.disclosureExtended || '';

      this.renderSummaryImage(panel);
      this.setCustomerError(valid ? '' : this.root.dataset.customerUnavailable);
      if (this.addButton) {
        this.addButton.disabled = !valid;
        this.addButton.setAttribute('aria-disabled', String(!valid));
      }
    }

    renderSummaryImage(panel) {
      const target = this.root.querySelector('[data-bp-summary-media]');
      if (!target) return;
      target.replaceChildren();
      const template = panel?.querySelector('[data-bp-product-image-template]');
      if (template) target.append(template.content.cloneNode(true));
    }

    setSummaryField(name, value) {
      const target = this.root.querySelector(`[data-bp-summary-field="${name}"]`);
      if (target) target.textContent = value || '';
    }

    getProductPanel(category, tier) {
      if (!category || !tier) return null;
      return this.productPanels.find(
        (panel) => panel.dataset.category === category && panel.dataset.tier === tier,
      ) || null;
    }

    getSelectedProductPanel() {
      return this.getProductPanel(this.state.category, this.state.tier);
    }

    categoryIsConfigured(category) {
      if (!category) return false;
      return Core.TIERS.some((tier) => this.getProductPanel(category, tier)?.dataset.configured === 'true');
    }

    getSelectedVariant() {
      const panel = this.getSelectedProductPanel();
      if (!panel || !this.state.size) return null;
      const input = Array.from(panel.querySelectorAll('[data-option-group="size"]')).find(
        (candidate) => candidate.value === this.state.size,
      );
      if (!input) return null;

      return {
        id: input.dataset.variantId,
        productKey: input.dataset.productKey,
        variantKey: input.dataset.variantKey,
        sizeLabel: input.dataset.sizeLabel,
        priceCents: input.dataset.priceCents,
        priceFormatted: input.dataset.priceFormatted,
        itemCountMin: input.dataset.itemCountMin,
        itemCountMax: input.dataset.itemCountMax,
        countMode: input.dataset.countMode,
        minimumValueCents: input.dataset.minimumValueCents,
        minimumValueFormatted: input.dataset.minimumValueFormatted,
        builderLabel: input.dataset.builderLabel,
        weightGrams: input.dataset.weightGrams,
        sku: input.dataset.sku,
        available: input.dataset.available === 'true',
        availabilityLabel: input.closest('.bp-mystery-option')?.querySelector('[data-bp-availability]')?.textContent?.trim() || '',
      };
    }

    formatQuantity(variant) {
      return Core.formatQuantity(variant.itemCountMin, variant.itemCountMax, this.labels().quantity);
    }

    labels() {
      return {
        quantity: {
          fixedOne: this.root.dataset.quantityOne,
          fixedOther: this.root.dataset.quantityOther,
          range: this.root.dataset.quantityRange,
        },
        publicQuantity: this.root.dataset.propertyQuantity,
        publicMinimumValue: this.root.dataset.propertyMinimumValue,
        publicSkinTone: this.root.dataset.propertySkinTone,
        publicUndertone: this.root.dataset.propertyUndertone,
        publicBaseFace: this.root.dataset.propertyBaseFace,
        skinTones: {
          'very-light': this.root.dataset.labelSkinVeryLight,
          light: this.root.dataset.labelSkinLight,
          medium: this.root.dataset.labelSkinMedium,
          'medium-deep': this.root.dataset.labelSkinMediumDeep,
          deep: this.root.dataset.labelSkinDeep,
          'very-deep': this.root.dataset.labelSkinVeryDeep,
          unknown: this.root.dataset.labelSkinUnknown,
        },
        undertones: {
          cool: this.root.dataset.labelUndertoneCool,
          neutral: this.root.dataset.labelUndertoneNeutral,
          warm: this.root.dataset.labelUndertoneWarm,
          olive: this.root.dataset.labelUndertoneOlive,
          unknown: this.root.dataset.labelUndertoneUnknown,
        },
        baseFaceExcluded: this.root.dataset.labelBaseExcluded,
        baseFaceNoExclusion: this.root.dataset.labelBaseNoExclusion,
      };
    }

    updateRequestCount() {
      if (this.requestField && this.requestCount) {
        this.requestCount.textContent = `${this.requestField.value.length} / ${this.requestField.maxLength}`;
      }
    }

    labelForCategory(category) {
      if (!category) return '';
      const key = `labelCategory${category.charAt(0).toUpperCase()}${category.slice(1)}`;
      return this.root.dataset[key] || '';
    }

    labelForTier(tier) {
      if (!tier) return '';
      const key = `labelTier${tier.charAt(0).toUpperCase()}${tier.slice(1)}`;
      return this.root.dataset[key] || '';
    }

    async addToCart() {
      await this.submissionGate.run(async () => {
        const panel = this.getSelectedProductPanel();
        const variant = this.getSelectedVariant();
        const validation = Core.validateVariantData(variant, {
          productConfigured: panel?.dataset.configured === 'true',
          requireAvailable: true,
        });

        if (!validation.valid) {
          this.handleCartError(validation.code, this.root.dataset.customerUnavailable);
          return;
        }

        const submittedRequestValue = this.requestField?.value || '';
        const requestText = this.requestField
          ? submittedRequestValue.slice(0, this.requestField.maxLength).replace(/[\uD800-\uDBFF]$/, '')
          : '';
        const request = requestText.trim() ? requestText : '';
        const submittedState = JSON.stringify(this.state);
        this.setPending(true);
        this.setAtelierSuccess(false);
        this.setCustomerError('');

        const formData = new FormData();
        formData.append('id', variant.id);
        formData.append('quantity', '1');

        const properties = Core.buildLineProperties(this.state, variant, this.labels(), BUILDER_VERSION);
        Object.entries(properties).forEach(([name, value]) => {
          formData.append(`properties[${name}]`, String(value));
        });
        if (request && this.requestField?.name) formData.append(this.requestField.name, request);

        const cart = document.querySelector('cart-drawer');
        const sections = cart && typeof cart.getSectionsToRender === 'function'
          ? cart.getSectionsToRender().map((section) => section.id)
          : ['cart-icon-bubble'];
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);

        const config = typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/javascript' } };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        try {
          cart?.setActiveElement?.(this.addButton);
          const response = await fetch(window.routes.cart_add_url, config);
          let payload;
          try {
            payload = await response.json();
          } catch (_error) {
            throw this.cartError('invalid_json', this.root.dataset.cartInvalidResponse);
          }

          if (!response.ok || payload.status) {
            const code = response.status === 422
              ? 'sold_out'
              : response.status === 401 || response.status === 403
                ? 'session_expired'
                : `http_${response.status || payload.status || 'error'}`;
            const fallbackMessage = code === 'sold_out'
              ? this.root.dataset.cartSoldOut
              : code === 'session_expired'
                ? this.root.dataset.cartSessionError
                : this.root.dataset.cartError;
            throw this.cartError(code, fallbackMessage);
          }

          this.publishCartUpdate(variant, payload);
          this.renderCartResult(cart, payload);
          if (JSON.stringify(this.state) === submittedState && (this.requestField?.value || '') === submittedRequestValue) {
            this.setCartStatus(this.root.dataset.cartSuccess);
            this.setAtelierSuccess(true);
          } else {
            this.setAtelierSuccess(false);
          }
          this.emit('bp:mystery_add_to_cart');
        } catch (error) {
          const code = error?.bpCode || (error instanceof TypeError ? 'network_error' : 'unknown_error');
          const message = error?.bpMessage
            || (code === 'network_error' ? this.root.dataset.cartNetworkError : this.root.dataset.cartError);
          this.handleCartError(code, message);
        } finally {
          this.setPending(false);
        }
      });
    }

    cartError(code, message) {
      const error = new Error(message);
      error.bpCode = code;
      error.bpMessage = message;
      return error;
    }

    publishCartUpdate(variant, payload) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'bp-mystery-builder',
        productVariantId: variant.id,
        cartData: payload,
      });
    }

    publishCartError(code, message, variantId) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'bp-mystery-builder',
        productVariantId: variantId || '',
        errors: message,
        message,
        code,
      });
    }

    renderCartResult(cart, payload) {
      const isCartPage = document.body.classList.contains('template-cart');
      const cartType = cart?.dataset.cartType || 'page';
      const sectionsComplete = cart?.getSectionsToRender?.().every(
        (section) => typeof payload.sections?.[section.id] === 'string',
      );

      if (cart && cartType === 'drawer' && !isCartPage && sectionsComplete) {
        cart.classList.remove('is-empty');
        const isClosedCart = !document.body.classList.contains('page-overlay-cart-on');
        cart.renderContents(payload, isClosedCart);
        return;
      }

      this.refreshCartBubble(payload);
      if (cart && !sectionsComplete) {
        this.root.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true, composed: true }));
      }
    }

    refreshCartBubble(payload) {
      const bubble = document.getElementById('cart-icon-bubble');
      const html = payload?.sections?.['cart-icon-bubble'];
      if (!bubble || !html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('.shopify-section') || parsed.body;
      if (source) bubble.innerHTML = source.innerHTML;
    }

    handleCartError(code, message) {
      const variant = this.getSelectedVariant();
      this.setCustomerError(message || this.root.dataset.cartError);
      this.setCartStatus(message || this.root.dataset.cartError);
      this.publishCartError(code, message, variant?.id);
      this.emit('bp:mystery_add_to_cart_error', { errorCode: code });
    }

    setPending(pending) {
      if (!this.addButton) return;
      const variant = this.getSelectedVariant();
      const valid = Core.validateVariantData(variant, {
        productConfigured: this.getSelectedProductPanel()?.dataset.configured === 'true',
        requireAvailable: true,
      }).valid;
      this.addButton.disabled = pending || !valid;
      this.addButton.setAttribute('aria-disabled', String(pending || !valid));
      this.addButton.setAttribute('aria-busy', String(pending));
      if (this.requestField) this.requestField.disabled = pending;
      const label = this.addButton.querySelector('[data-bp-add-label]');
      if (label) {
        label.textContent = pending ? this.addButton.dataset.labelPending : this.addButton.dataset.labelDefault;
      }
    }

    setCustomerError(message) {
      if (!this.customerError) return;
      this.customerError.textContent = message || '';
      this.customerError.hidden = !message;
    }

    setCartStatus(message) {
      if (this.cartStatus) this.cartStatus.textContent = message || '';
    }

    writeHistory(mode) {
      if (this.isDesignMode || !window.history?.[`${mode}State`]) return;
      const current = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
      const next = {
        ...current,
        bpMysteryBuilder: {
          version: BUILDER_VERSION,
          sectionId: this.sectionId,
          view: this.currentView,
          state: this.state,
        },
      };
      window.history[`${mode}State`](next, document.title, window.location.href);
    }

    handlePopState(event) {
      const record = event.state?.bpMysteryBuilder;
      if (!record || record.sectionId !== this.sectionId || record.version !== BUILDER_VERSION) return;
      this.state = Core.sanitizeState(record.state);
      this.persistState();
      this.showView(record.view, { focus: true, announce: true, emit: true });
    }

    readStoredState() {
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || '{}');
        return Core.sanitizeState(stored);
      } catch (_error) {
        return Core.sanitizeState();
      }
    }

    persistState() {
      if (this.isDesignMode) return;
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (_error) {
        // The in-page flow remains functional when session storage is unavailable.
      }
    }

    interpolate(template, values) {
      return Object.entries(values).reduce(
        (output, [key, value]) => String(output || '').split(`__${key}__`).join(String(value)),
        template,
      );
    }

    emit(eventName, extra = {}) {
      const variant = this.getSelectedVariant();
      this.root.dispatchEvent(
        new CustomEvent(eventName, {
          bubbles: true,
          detail: Core.buildAnalyticsPayload(this.state, variant, extra),
        }),
      );
    }

    handleSectionSelect() {
      if (!this.isDesignMode) return;
      this.showView(this.root.dataset.previewStep, { focus: false, announce: false, emit: false });
      this.root.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    }

    handleSectionDeselect() {
      // No automatic animation or timer needs to resume.
    }

    destroy() {
      this.dockObserver?.disconnect();
      this.visualAnimations.forEach(animation => animation.cancel());
      this.visualAnimations.clear();
      this.journey?.destroy();
      this.abortController.abort();
      window.BPUniverse?.destroy(this.root);
      this.root.classList.remove('is-ready');
      delete this.root.dataset.bpBuilderReady;
      instances.delete(this.root);
      instancesBySectionId.delete(this.sectionId);
    }
  }

  const rootsWithin = (container) => {
    const roots = [];
    if (container instanceof Element && container.matches(rootSelector)) roots.push(container);
    container.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    return roots;
  };

  const initialize = (container = document) => {
    rootsWithin(container).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new MysteryBuilder(root);
      instances.set(root, instance);
      instancesBySectionId.set(instance.sectionId, instance);
      instance.initialize();
    });
  };

  const destroy = (container) => {
    rootsWithin(container).forEach((root) => instances.get(root)?.destroy());
  };

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    instancesBySectionId.get(String(event.detail?.sectionId || ''))?.handleSectionSelect();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    instancesBySectionId.get(String(event.detail?.sectionId || ''))?.handleSectionDeselect();
  });

  window.BPMysteryBuilder = Object.freeze({ initialize, destroy, version: BUILDER_VERSION });
  initialize(document);
})();
